import { useState, useEffect, useCallback, useMemo, useRef, useContext, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlusCircle, Pin, PinOff, X, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipPortal } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { SidebarContext } from '@/contexts/SidebarContext';
import { useLayoutState } from '@/hooks/useLayoutState';
import { useDomain } from '@/lib/domain-context';
import { usePrefetchIssueSpotter } from '@/hooks/use-query-issue-spotter';
import { useUserRole } from '@/hooks/useUserRole';
import { useCommunityHasUnread } from '@/hooks/use-community';

// Sub-components
import { SidebarNavItem } from '@/components/sidebar/SidebarNavItem';
import { SidebarNavGroup } from '@/components/sidebar/SidebarNavGroup';
import { SidebarAskJDS } from '@/components/sidebar/SidebarAskJDS';
import { SidebarUserMenu } from '@/components/sidebar/SidebarUserMenu';
import { getNavigationEntries, getAdminNavigationEntries } from '@/components/sidebar/navigation-config';

interface SidebarProps {
  setActiveTab: (tab: string | null) => void;
  isDesktopExpanded: boolean;
  onDesktopExpandedChange: (expanded: boolean) => void;
  isPinned?: boolean;
  onPinChange?: (pinned: boolean) => void;
  onNewChat: () => void;
  onDeleteThread: (id: string) => void;
  onRenameThread: (id: string, newTitle: string) => void;
  sessions: {
    id: string;
    title: string;
    created_at: string;
  }[];
  currentSession: string | null;
}

export const Sidebar = memo(function Sidebar({
  setActiveTab,
  isDesktopExpanded,
  onDesktopExpandedChange,
  isPinned: isPinnedProp,
  onPinChange,
  onNewChat,
  onDeleteThread,
  onRenameThread,
  sessions,
  currentSession,
}: SidebarProps) {
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isExpanded, setIsExpanded, isMobile } = useContext(SidebarContext);
  const { isAskJDS, isJDSimplified, isAdmin, isStudicata } = useDomain();
  const prefetchIssueSpotter = usePrefetchIssueSpotter();
  const { data: roleData } = useUserRole();

  // Pin state
  const { isPinned, setIsPinned } = useLayoutState();
  const effectiveIsPinned = isPinnedProp !== undefined ? isPinnedProp : isPinned;

  // Group collapse state (default true = collapsed; admin expanded on admin domain)
  const [collapsedGroups, setCollapsedGroups] = usePersistedState<Record<string, boolean>>(
    'sidebar-collapsed-groups',
    { practice: true, library: true, community: true, nextgen: true, admin: !isAdmin }
  );

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const isCurrentlyCollapsed = prev[groupId] ?? true;
      if (isCurrentlyCollapsed) {
        // Opening this group — collapse all others to save vertical space
        const next: Record<string, boolean> = {};
        for (const key of Object.keys(prev)) {
          next[key] = key === groupId ? false : true;
        }
        // Ensure target group is set even if not yet in persisted state
        next[groupId] = false;
        return next;
      }
      // Closing this group — just toggle it
      return { ...prev, [groupId]: true };
    });
  }, [setCollapsedGroups]);

  // Community unread dot for sidebar
  const { data: communityHasUnread } = useCommunityHasUnread();

  // Navigation config — includes admin entries when on admin domain
  const navEntries = useMemo(() => {
    const entries = getNavigationEntries({
      isAskJDS,
      isJDSimplified,
      isAdmin,
      isStudicata,
      // Role-based override: admin/partner users see NextGen on live (jdsimplified.com).
      // While roleData is loading, both flags are falsy → group is hidden on live and
      // pops in once the query resolves. ~100-300ms flicker for ~3 admin users.
      isAdminUser: roleData?.isAdmin ?? false,
      isPartnerUser: roleData?.isPartner ?? false,
      prefetchIssueSpotter,
    });
    // Inject unread dot into community group (immutable — no mutation of returned objects)
    const withDot = communityHasUnread
      ? entries.map((entry) =>
          entry.type === 'group' && entry.group.id === 'community'
            ? { ...entry, group: { ...entry.group, showDot: true } }
            : entry
        )
      : entries;
    if (isAdmin) {
      const adminEntries = getAdminNavigationEntries({
        isAdmin: roleData?.isAdmin ?? false,
        isStaff: roleData?.isStaff ?? false,
      });
      return [...withDot, ...adminEntries];
    }
    return withDot;
  }, [isAskJDS, isJDSimplified, isAdmin, isStudicata, prefetchIssueSpotter, roleData?.isAdmin, roleData?.isPartner, roleData?.isStaff, communityHasUnread]);

  // --- Expand/collapse sync (preserved from original) ---
  useEffect(() => {
    if (isDesktopExpanded !== isExpanded) {
      setIsExpanded(isDesktopExpanded);
    }
  }, [isDesktopExpanded, setIsExpanded]);

  useEffect(() => {
    if (isExpanded !== isDesktopExpanded) {
      onDesktopExpandedChange(isExpanded);
    }
  }, [isExpanded, onDesktopExpandedChange, isDesktopExpanded]);

  useEffect(() => {
    if (effectiveIsPinned && !isDesktopExpanded) {
      onDesktopExpandedChange(true);
    }
  }, [effectiveIsPinned, isDesktopExpanded, onDesktopExpandedChange]);

  useEffect(() => {
    if (isMobile && effectiveIsPinned) {
      setIsPinned(false);
    }
  }, [isMobile, effectiveIsPinned, setIsPinned]);

  // --- Pin toggle ---
  const recentlyToggledPinRef = useRef(false);

  const togglePin = () => {
    if (isMobile) return;
    const newPinState = !effectiveIsPinned;
    setIsPinned(newPinState);
    recentlyToggledPinRef.current = true;
    setTimeout(() => {
      recentlyToggledPinRef.current = false;
    }, 300);
    if (onPinChange) onPinChange(newPinState);
    setIsExpanded(newPinState);
    onDesktopExpandedChange(newPinState);
  };

  // --- Mouse hover expand/collapse ---
  const handleMouseEnter = useCallback(() => {
    if (recentlyToggledPinRef.current) return;
    if (!isMobile && !effectiveIsPinned) {
      onDesktopExpandedChange(true);
      setIsExpanded(true);
    }
  }, [isMobile, effectiveIsPinned, onDesktopExpandedChange, setIsExpanded]);

  const handleMouseLeave = useCallback(() => {
    if (recentlyToggledPinRef.current) return;
    if (isContextMenuOpen) return;
    if (!isMobile && !effectiveIsPinned) {
      onDesktopExpandedChange(false);
      setIsExpanded(false);
    }
  }, [isMobile, effectiveIsPinned, onDesktopExpandedChange, setIsExpanded, isContextMenuOpen]);

  // --- Navigation handler ---
  const handleNavLinkClick = useCallback(
    (path: string) => {
      navigate(path, { replace: false, state: { fromSidebar: true } });
      if (isMobile) {
        setIsExpanded(false);
        onDesktopExpandedChange(false);
      }
    },
    [isMobile, onDesktopExpandedChange, setIsExpanded, navigate]
  );

  // --- Mobile close ---
  const handleMobileClose = useCallback(() => {
    if (effectiveIsPinned) {
      setIsPinned(false);
      if (onPinChange) onPinChange(false);
    }
    onDesktopExpandedChange(false);
    setIsExpanded(false);
    setTimeout(() => {
      document.body.classList.remove('sidebar-open');
      const sidebar = document.querySelector('.sidebar-container');
      if (sidebar) {
        sidebar.classList.add('sidebar-hidden-mobile', '-translate-x-full', 'opacity-0', 'pointer-events-none');
      }
    }, 50);
  }, [effectiveIsPinned, setIsPinned, onPinChange, onDesktopExpandedChange, setIsExpanded]);

  return (
    <div
      role="navigation"
      aria-label="Main navigation"
      className={cn(
        'flex flex-col bg-white dark:bg-gray-800 border-r transition-all duration-300 sidebar-transition sidebar-container h-full',
        !isMobile && 'z-50',
        !isMobile && (isDesktopExpanded ? 'w-[var(--sidebar-width)] expanded' : 'w-[var(--sidebar-collapsed-width)] collapsed'),
        isMobile && !isDesktopExpanded ? 'opacity-0 pointer-events-none w-0 -translate-x-full sidebar-hidden-mobile' : '',
        isMobile && isDesktopExpanded ? 'w-[var(--sidebar-width)] shadow-xl expanded z-[60] transform translate-x-0 opacity-100' : ''
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ overflow: 'hidden' }}
      data-mobile={isMobile}
      data-expanded={isDesktopExpanded}
    >
      {/* Header: Logo + mobile close */}
      <div className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className={cn('flex items-center justify-center py-3', isDesktopExpanded ? 'px-4' : 'px-2')}>
          {isMobile && isDesktopExpanded && (
            <button
              onClick={handleMobileClose}
              className="absolute right-2 p-3 rounded-md text-muted-foreground hover:bg-muted dark:hover:bg-gray-700 bg-background/80 dark:bg-gray-800/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {isDesktopExpanded ? (
            <>
              <img
                data-twic-src="media:images/JDSimplified_Logo.png"
                data-twic-transform="quality=95/output=auto"
                alt="JD Simplified Logo"
                className="h-10 transition-all dark:hidden"
              />
              <img
                data-twic-src="media:images/JDSimplified_Logo_wht.png"
                data-twic-transform="quality=95/output=auto"
                alt="JD Simplified Logo"
                className="h-10 transition-all hidden dark:block"
              />
            </>
          ) : (
            <img
              data-twic-src="media:images/JD Simplified Favicon.svg"
              data-twic-transform="quality=95/output=auto"
              alt="JDS"
              className="h-10 transition-all dark:invert"
            />
          )}
        </div>
      </div>

      {/* Action Plan button + New Chat icon + Pin toggle */}
      <div className="p-2 border-b dark:border-gray-700 flex items-center gap-1 bg-white dark:bg-gray-800">
        <TooltipProvider delayDuration={0}>
        {isDesktopExpanded ? (
          <>
            <button
              onClick={() => handleNavLinkClick('/dashboard')}
              className={cn(
                'flex font-medium items-center gap-2 px-3 py-2 flex-1 min-w-0 rounded-lg transition min-h-[44px] md:min-h-0',
                location.pathname === '/dashboard'
                  ? 'bg-[#f37022] text-white hover:bg-[#e36012]'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700',
                'justify-start',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
              )}
            >
              <ClipboardList className="h-4 w-4 shrink-0" />
              <span className="transition-all duration-300 truncate font-medium">
                Action Plan
              </span>
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => onNewChat()}
                  size="icon"
                  variant="ghost"
                  className="shrink-0 text-[#F37022] hover:text-[#e36012] hover:bg-orange-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent side="bottom" className="bg-gray-900 text-white border-0 dark:border-gray-700" style={{ zIndex: 99999 }}>
                  New Chat
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => onNewChat()}
                size="icon"
                variant="ghost"
                className="flex-1 text-[#F37022] hover:text-[#e36012] hover:bg-orange-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
              >
                <PlusCircle className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right" className="bg-gray-900 text-white border-0 dark:border-gray-700" style={{ zIndex: 99999 }}>
                New Chat
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={togglePin}
                size="icon"
                variant="ghost"
                className={cn(
                  'shrink-0',
                  (!isDesktopExpanded || isMobile) && 'hidden',
                  'dark:hover:bg-gray-700',
                  effectiveIsPinned && 'text-[#F37022]',
                  !effectiveIsPinned && 'dark:text-gray-300 dark:hover:text-gray-400',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
                )}
              >
                {effectiveIsPinned ? <Pin className="h-4 w-4" /> : <PinOff className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="bottom" className="bg-gray-900 text-white border-0 dark:border-gray-700" style={{ zIndex: 99999 }}>
                {effectiveIsPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Main content — flex layout: threads fill available space, nav pinned below */}
      <div className="flex-1 min-h-0 flex flex-col p-2 space-y-1 overflow-y-auto">
        {/* Ask JDS + threads (threads grow to fill space) */}
        <SidebarAskJDS
          sessions={sessions}
          currentSession={currentSession}
          isDesktopExpanded={isDesktopExpanded}
          setActiveTab={setActiveTab}
          onDeleteThread={onDeleteThread}
          onRenameThread={onRenameThread}
          onDesktopExpandedChange={onDesktopExpandedChange}
          onContextMenuOpenChange={setIsContextMenuOpen}
        />

        {/* Bottom nav — pushed down, scrolls if needed */}
        <div className="mt-auto pt-1 space-y-1">
          {navEntries.map((entry) => {
            if (entry.type === 'separator') {
              return null;
            }
            if (entry.type === 'group') {
              return (
                <SidebarNavGroup
                  key={entry.group.id}
                  group={entry.group}
                  isExpanded={isDesktopExpanded}
                  isCollapsed={collapsedGroups[entry.group.id] ?? true}
                  onToggleCollapse={() => toggleGroup(entry.group.id)}
                  onNavClick={handleNavLinkClick}
                />
              );
            }
            return (
              <SidebarNavItem
                key={entry.item.href}
                item={entry.item}
                isExpanded={isDesktopExpanded}
                onClick={handleNavLinkClick}
              />
            );
          })}
        </div>
      </div>

      {/* User menu (sticky bottom) */}
      <SidebarUserMenu isExpanded={isDesktopExpanded} onDropdownOpenChange={setIsContextMenuOpen} />
    </div>
  );
});
